import { getMe } from 'core/API/broadcast';
import { GetOrganizations } from 'core/API/workplaces';
import { useWorkspaceResources } from 'core/hooks/broadcast';
import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { OrganizationConfig, UserMe } from 'types';
import { useWorkplacesContext } from '../workplaces/context/WorkplacesContext';
import useUserDataStore from 'store/useUserDataStore';
import { SecurityRole as SecurityRoleType } from 'types';
import { resolveLoggedInWorkplaceUser } from 'core/utils/resolveLoggedInWorkplaceUser';

/**
 * Resolves the logged-in workplace user from /me + workplace users,
 * and keeps security role / localStorage user in sync.
 */
export const useWorkplaceUser = () => {
	const { data: usersData, isLoading: isUsersLoading } = useWorkspaceResources();
	const { workplace } = useWorkplacesContext();
	const { setCurrentUserRole } = useUserDataStore();

	const data = useQuery<UserMe>('me', getMe, {
		staleTime: Infinity,
		refetchOnWindowFocus: false,
		onSuccess: (data) => {
			const storedData = localStorage.getItem('BYOD-app');
			if (storedData) {
				try {
					const parsedData = JSON.parse(storedData);
					parsedData.user = data;
					localStorage.setItem('BYOD-app', JSON.stringify(parsedData));
				} catch (error) {
					console.error('Failed to update user data in localStorage:', error);
				}
			}

			if (data?.securityRole) {
				const sr = data.securityRole as SecurityRoleType;
				const roleResponse = {
					...sr,
					id: parseInt(sr['@id'].split('/').pop() || '0', 10),
					permissions: [],
					active: !sr.deleted,
				};
				setCurrentUserRole(roleResponse);
			}
		},
	});

	const { data: userInfo, isLoading: isMeLoading } = data;

	const { data: orgData } = useQuery<OrganizationConfig>('org', GetOrganizations, {
		staleTime: Infinity,
		refetchOnWindowFocus: false,
	});

	const userOrg = orgData?.[0];

	const currentUser = useMemo(
		() => resolveLoggedInWorkplaceUser(userInfo, usersData),
		[usersData, userInfo]
	);

	return {
		data: currentUser,
		isLoading: isMeLoading || isUsersLoading,
		userOrg,
		workplace,
		securityRole: userInfo?.securityRole as SecurityRoleType,
	};
};
